import React, { Fragment, ReactNode } from 'react';
import moment from 'moment';
import {
  ChatMessage,
  ChatMessageType,
  CardMessageContent,
  BoxMessageContent,
  FileMessageContent,
  CardMessageBtnInfo,
} from '@models/chat';
import { cdnImageUrl } from '@utils/image';
import DefaultAvatar from '@assets/images/avatar/default-avatar.png';
import { VerticalAlignBottomOutlined } from '@ant-design/icons';

// Props definitions
interface ChatBubbleV2Props {
  message: ChatMessage;
  isMe: boolean;
  isDifferentUser: boolean;
  isNeedDateLine: boolean;
  profileImage?: string;
  senderName?: string;
}

interface CardMessageButtonProps {
  info: CardMessageBtnInfo;
}

// Sub-components from igogo-web ChatPage
const DateLine: React.FC<{ date: Date }> = ({ date }) => (
  <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
    <hr
      style={{
        position: 'absolute',
        top: '50%',
        margin: 0,
        width: '100%',
        height: '1px',
        border: 'none',
        backgroundColor: '#F5F7FB',
      }}
    />
    <div style={{ display: 'inline', color: '#9B9DA0', backgroundColor: 'white', fontSize: '12px', zIndex: 1 }}>
      {moment(date).format('YYYY년 MM월 DD일')}
    </div>
  </div>
);

const CardMessageButton: React.FC<CardMessageButtonProps> = ({ info }) => {
  return (
    <button
      key={info.label}
      style={{
        padding: '10px',
        backgroundColor: '#EFF1F4',
        border: 'none',
        borderRadius: '6px',
        fontSize: '12px',
        cursor: 'pointer',
        width: '100%',
        textAlign: 'center',
      }}
      onClick={() => alert(`'${info.label}' 버튼 클릭 (어드민 기능 미연동)`)}>
      {info.label}
    </button>
  );
};

const CardMessage: React.FC<CardMessageContent> = (props) => {
  const { title, content, headerIcon, imageUrl, tagInfo, btnInfos } = props;

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '260px',
        border: '1px solid rgba(0, 0, 0, 0.10)',
        borderRadius: '10px',
        overflow: 'hidden',
        backgroundColor: 'white',
      }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px',
          backgroundColor: '#242424',
        }}>
        <div style={{ color: 'white', fontSize: '16px', fontWeight: 'bold', lineHeight: '27px' }}>{title}</div>
        {headerIcon && <img width={24} height={24} src={headerIcon} alt="card-header-icon" />}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '10px' }}>
        {imageUrl && (
          <img
            src={cdnImageUrl(imageUrl)}
            alt="card-image"
            style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '4px' }}
          />
        )}
        <div style={{ lineHeight: '120%', whiteSpace: 'pre-wrap' }}>{content}</div>
        {tagInfo && (
          <div
            style={{
              paddingLeft: '6px',
              color: '#66686B',
              borderLeft: '2px solid #E8EAED',
              fontSize: '13px',
            }}>
            {tagInfo.text}
          </div>
        )}
        {btnInfos && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {btnInfos.map((info, index) => (
              <CardMessageButton key={index} info={info} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const BoxMessage: React.FC<BoxMessageContent> = (props) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      width: '100%',
      padding: '20px',
      backgroundColor: '#FFF5F9',
      borderRadius: '12px',
    }}>
    <div style={{ margin: 0, color: '#FF3D8F', fontSize: '15px', fontWeight: 700 }}>{props.title}</div>
    <ul
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        margin: 0,
        paddingLeft: '20px',
        color: '#424242',
        fontSize: '13px',
        whiteSpace: 'pre-wrap',
      }}>
      {props.messages.map((message, idx) => (
        <li key={`message-${idx}`} style={{ lineHeight: '18px' }}>
          {message}
        </li>
      ))}
    </ul>
  </div>
);

const ImageMessage: React.FC<{ imageUrls: string[] }> = ({ imageUrls }) => (
  <div style={{ display: 'grid', gap: '4px', gridTemplateColumns: imageUrls.length > 1 ? '1fr 1fr' : '1fr' }}>
    {(imageUrls || []).map((imageUrl: string) => (
      <img
        key={imageUrl}
        src={cdnImageUrl(imageUrl)}
        alt="chat-image"
        style={{ maxWidth: '120px', borderRadius: '8px', objectFit: 'cover' }}
      />
    ))}
  </div>
);

const FileMessage: React.FC<FileMessageContent & { isMe: boolean }> = ({ isMe, originName, fileUrl }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '10px 16px',
      color: isMe ? '#242424' : 'white',
      backgroundColor: isMe ? '#f0f0f0' : '#1890ff',
      borderRadius: '22px',
    }}>
    <p style={{ margin: 0, wordBreak: 'break-all', textOverflow: 'ellipsis' }}>{originName}</p>
    <a
      href={cdnImageUrl(fileUrl)}
      download
      target="_blank"
      rel="noreferrer"
      style={{ color: isMe ? '#242424' : 'white' }}>
      <VerticalAlignBottomOutlined />
    </a>
  </div>
);

// Main Component (previously ChatBubbleV2, now acting as MessageContainer)
const ChatBubbleV2: React.FC<ChatBubbleV2Props> = (props) => {
  const { message, profileImage, isMe, isDifferentUser, isNeedDateLine, senderName } = props;
  const isNeedProfileImage = !isMe && isDifferentUser;
  let messageContent: ReactNode;

  switch (message.data.type) {
    case ChatMessageType.TEXT:
      messageContent = (
        <div
          style={{
            padding: '10px 16px',
            color: isMe ? 'white' : '#242424',
            backgroundColor: isMe ? '#1890ff' : '#f0f0f0',
            borderRadius: '22px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}>
          {message.data.content}
        </div>
      );
      break;
    case ChatMessageType.IMAGE:
      messageContent = <ImageMessage imageUrls={message.data.content as string[]} />;
      break;
    case ChatMessageType.CARD:
      messageContent = <CardMessage {...(message.data.content as CardMessageContent)} />;
      break;
    case ChatMessageType.BOX:
      messageContent = <BoxMessage {...(message.data.content as BoxMessageContent)} />;
      break;
    case ChatMessageType.FILE:
      messageContent = <FileMessage {...(message.data.content as FileMessageContent)} isMe={isMe} />;
      break;
    default:
      messageContent = <div>지원하지 않는 메시지 타입입니다.</div>;
      break;
  }

  // BoxMessage should not be wrapped in a bubble
  if (message.data.type === ChatMessageType.BOX) {
    return (
      <Fragment>
        {isNeedDateLine && <DateLine date={message.createdAt} />}
        <div style={{ margin: '8px 0' }}>{messageContent}</div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {isNeedDateLine && <DateLine date={message.createdAt} />}
      <div style={{ display: 'flex', flexDirection: isMe ? 'row-reverse' : 'row', gap: '6px', margin: '8px 0' }}>
        {isNeedProfileImage && (
          <img
            src={profileImage ? cdnImageUrl(profileImage) : DefaultAvatar}
            alt="profile"
            width={36}
            height={36}
            style={{ flexShrink: 0, borderRadius: '50%' }}
          />
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            alignItems: isMe ? 'flex-end' : 'flex-start',
            width: message.data.type === ChatMessageType.CARD ? '100%' : 'auto',
            marginLeft: isNeedProfileImage || isMe ? '0px' : '42px',
          }}>
          {!isMe && isDifferentUser && (
            <div style={{ fontSize: '12px', color: '#888', marginLeft: '4px' }}>{senderName}</div>
          )}
          {messageContent}
        </div>
        <div
          style={{
            alignSelf: 'flex-end',
            flexShrink: 0,
            marginBottom: '2px',
            color: '#808387',
            fontSize: '9px',
          }}>
          <div>{moment(message.createdAt).format('HH:mm')}</div>
        </div>
      </div>
    </Fragment>
  );
};

export default ChatBubbleV2;
